import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { DICTS, DEFAULT_LANG, STORAGE_KEY, SUPPORTED, resolveInitialLang } from "@/i18n";

const LanguageContext = createContext(null);

const getPath = (obj, path) =>
    path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);

const interpolate = (str, vars) => {
    if (!vars) return str;
    return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
};

export const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState(() => resolveInitialLang());

    // Persist + reflect on <html lang> for a11y/SEO. Runs on mount too, so the
    // resolved language is written to localStorage for non-React consumers (apiClient).
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
        if (typeof document !== "undefined") document.documentElement.lang = lang;
    }, [lang]);

    // Keep language in sync across tabs/windows.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY && e.newValue && SUPPORTED.includes(e.newValue)) {
                setLang(e.newValue);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const changeLanguage = useCallback((code) => {
        if (SUPPORTED.includes(code)) setLang(code);
    }, []);

    // t("a.b.c", { name }) -> string (interpolated) | array | object (returned as-is).
    // Falls back to the German dictionary, then to the key itself.
    const t = useCallback((path, vars) => {
        const dict = DICTS[lang] || DICTS[DEFAULT_LANG];
        let val = getPath(dict, path);
        if (val === undefined) val = getPath(DICTS[DEFAULT_LANG], path);
        if (val === undefined) return path;
        if (typeof val === "string") return interpolate(val, vars);
        return val;
    }, [lang]);

    const value = useMemo(() => ({ lang, changeLanguage, t }), [lang, changeLanguage, t]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
    return ctx;
};

export const useTranslation = () => {
    const { t, lang, changeLanguage } = useLanguage();
    return { t, lang, changeLanguage };
};
